import React, { useEffect, useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { subscribeUser } from "../../../redux/actions/subscriptionAction";
import { useNavigate } from "react-router-dom";
import {
  AccountVerificationModal,
  LoadingSpinner,
  NotificationModal,
  Pricing,
} from "../../allComponents/AllComponents";
import { buttonColors, colors } from "../../../utility/types/constant";
import {
  ISubscription,
  SubscriptionStatus,
} from "../../../utility/types/subscriptionType";
import { Field, Form, Formik } from "formik";
import { validateSubscriptionForm } from "../../../utility/validation/subscriptionValidation";
import { STRIPE_TEST_MODE_PLANID } from "../../../axiosConfig/apiUrl";
import {
  // selectError,
  selectIsLoading,
  selectUserSubscription,
} from "../../../redux/selectors/subscriptionSelector";
import { StyledTypography } from "../../circle-leader-application/styles";
import country from "country-list-js";

interface SubscriptionModalProps {
  open: boolean;
  handleClose: () => void;
}

const planId = STRIPE_TEST_MODE_PLANID;

const SubscriptionModal = (props: SubscriptionModalProps) => {
  const userSubscription = useSelector(selectUserSubscription);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const stripe = useStripe();
  const elements = useElements();
  const { open, handleClose } = props;
  const userData = useSelector(selectCurrentUser);
  const isLoading = useSelector(selectIsLoading);
  const [error, setError] = useState<string | null>(null);

  const initialValues: ISubscription = {
    userId: userData?._id,
    firstName: "",
    lastName: "",
    address: {
      streetName: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      county: "",
    },
  };

  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    if (error) {
      setAlert({ type: "error", message: error });
      setError(error);
    }
  }, [error]);

  const handleStripeSubmit = async (
    values: ISubscription,
    { setSubmitting }: any
  ) => {
    if (!stripe || !elements) {
      setAlert({
        type: "error",
        message: "Stripe.js hasn't loaded yet.",
      });

      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setAlert({
        type: "error",
        message:
          "Card details are incomplete. Please provide full card details.",
      });

      return;
    }

    const countryName = values.address?.country ?? "";

    const countryData = country.findByName(countryName);

    const countryCode = countryData ? countryData.code.iso2 : "";

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          address: {
            city: values.address?.city ?? "",
            country: countryCode,
            line1: values.address?.streetName ?? "",
            postal_code: values.address?.zipCode ?? "",
            state: values.address?.state ?? "",
          },
          email: userData?.email,
          name: `${userData?.firstName} ${userData?.lastName}`,
        },
      });

      if (error) throw new Error(error.message);

      const payload = {
        ...values,
        paymentMethodId: paymentMethod?.id,
        planId: planId,
      };

      const result = await dispatch(subscribeUser({ payload })).unwrap();

      if (result.subscriptionStatus === SubscriptionStatus.active) {
        setAlert({ type: "success", message: "Subscription successful!" });
        setOpenNotificationModal(true);

        setTimeout(() => {
          setOpenNotificationModal(false);
          handleClose();
          navigate(`/user/${userData?._id}`, { replace: true });
        }, 5000);
      } else {
        setAlert({ type: "error", message: "Subscription failed" });
        setOpenNotificationModal(false);
      }
    } catch (error: any) {
      console.error("Failed to process payment:", error.message);
      setAlert({
        type: "error",
        message: error.message || "Error processing payment. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="subscription-modal-title"
        aria-describedby="subscription-modal-description"
        fullWidth
        maxWidth="xl"
      >
        {
          (!userSubscription ||
            userSubscription.subscriptionStatus ===
              SubscriptionStatus.cancelled) && (
            <DialogContent>
              <Grid
                container
                spacing={3}
                alignItems="flex-start"
                justifyContent="center"
              >
                {/* Title */}
                <Grid item xs={12}>
                  <Typography variant="h6" align="center" gutterBottom>
                    Complete Your Subscription
                  </Typography>
                </Grid>

                {/* Pricing Component */}
                <Grid item xs={12} md={8}>
                  <Pricing />
                </Grid>

                {/* Card Details */}
                <Grid item xs={12} md={4}>
                  <Card
                    raised
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <CardHeader
                      title="Payment Details"
                      subheader="Securely enter your card information"
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Formik
                        initialValues={initialValues}
                        onSubmit={handleStripeSubmit}
                        validationSchema={validateSubscriptionForm}
                      >
                        {({ errors, values, touched, handleChange }) => (
                          <Form
                            style={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <TextField
                              id="firstName"
                              name="firstName"
                              label="First Name"
                              fullWidth
                              variant="standard"
                              required
                              value={values.firstName}
                              onChange={handleChange}
                              margin="normal"
                              error={touched.firstName && !!errors.firstName}
                              helperText={touched.firstName && errors.firstName}
                            />
                            <TextField
                              id="lastName"
                              required
                              label="Last Name"
                              name="lastName"
                              variant="standard"
                              fullWidth
                              value={values.lastName}
                              onChange={handleChange}
                              margin="normal"
                              error={touched.lastName && !!errors.lastName}
                              helperText={touched.lastName && errors.lastName}
                            />

                            <Field name="address.streetName">
                              {({ field, form }: any) => (
                                <TextField
                                  {...field} // automatically handles `name`, `value`, `onChange`, `onBlur`
                                  id="streetName"
                                  label="Street Name"
                                  variant="standard"
                                  fullWidth
                                  required
                                  margin="normal"
                                  error={
                                    form.touched.address?.streetName &&
                                    Boolean(form.errors.address?.streetName)
                                  }
                                  helperText={
                                    form.touched.address?.streetName &&
                                    form.errors.address?.streetName
                                  }
                                />
                              )}
                            </Field>

                            <Field name="address.city">
                              {({ field, form }: any) => (
                                <TextField
                                  {...field} // automatically handles `name`, `value`, `onChange`, `onBlur`
                                  id="city"
                                  label="City"
                                  variant="standard"
                                  fullWidth
                                  required
                                  margin="normal"
                                  error={
                                    form.touched.address?.city &&
                                    Boolean(form.errors.address?.city)
                                  }
                                  helperText={
                                    form.touched.address?.city &&
                                    form.errors.address?.city
                                  }
                                />
                              )}
                            </Field>

                            <Field name="address.state">
                              {({ field, form }: any) => (
                                <TextField
                                  {...field} // automatically handles `name`, `value`, `onChange`, `onBlur`
                                  id="state"
                                  label="State/Province/Region"
                                  variant="standard"
                                  fullWidth
                                  required
                                  margin="normal"
                                  error={
                                    form.touched.address?.state &&
                                    Boolean(form.errors.address?.state)
                                  }
                                  helperText={
                                    form.touched.address?.state &&
                                    form.errors.address?.state
                                  }
                                />
                              )}
                            </Field>

                            <Field name="address.zipCode">
                              {({ field, form }: any) => (
                                <TextField
                                  {...field} // automatically handles `name`, `value`, `onChange`, `onBlur`
                                  id="zipCode"
                                  label="Zip Code"
                                  variant="standard"
                                  fullWidth
                                  required
                                  margin="normal"
                                  error={
                                    form.touched.address?.zipCode &&
                                    Boolean(form.errors.address?.zipCode)
                                  }
                                  helperText={
                                    form.touched.address?.zipCode &&
                                    form.errors.address?.zipCode
                                  }
                                />
                              )}
                            </Field>

                            <Field name="address.country">
                              {({ field, form }: any) => (
                                <TextField
                                  {...field} // automatically handles `name`, `value`, `onChange`, `onBlur`
                                  id="country"
                                  label="Country"
                                  variant="standard"
                                  fullWidth
                                  required
                                  margin="normal"
                                  error={
                                    form.touched.address?.country &&
                                    Boolean(form.errors.address?.country)
                                  }
                                  helperText={
                                    form.touched.address?.country &&
                                    form.errors.address?.country
                                  }
                                />
                              )}
                            </Field>

                            <Box component="div" sx={{ mt: 3, mb: 2 }}>
                              <CardElement
                                options={{
                                  style: {
                                    base: {
                                      fontSize: "16px",
                                      color: "#424770",

                                      "::placeholder": { color: "#aab7c4" },
                                    },
                                    invalid: { color: "#9e2146" },
                                  },
                                }}
                              />
                            </Box>
                            <Button
                              type="submit"
                              disabled={!stripe || isLoading}
                              variant="contained"
                              sx={{
                                mt: 2,
                                color: colors.white,
                                backgroundColor: buttonColors.infoDarkBlue,
                                "&:hover": {
                                  backgroundColor: buttonColors.infoDarkBlue,
                                },
                              }}
                            >
                              {isLoading ? (
                                <LoadingSpinner
                                  loading={isLoading}
                                  color={colors.white}
                                />
                              ) : (
                                "Subscribe"
                              )}
                            </Button>
                            {alert?.type === "error" && (
                              <div style={{ color: "red" }}>
                                {
                                  "An error occurred while processing payment. Please try again."
                                }
                              </div>
                            )}
                            {alert?.type === "success" && (
                              <div style={{ color: "green" }}>
                                {alert.message}
                              </div>
                            )}
                          </Form>
                        )}
                      </Formik>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
          )
          //)
        }
      </Dialog>

      {openNotificationModal && (
        <NotificationModal
          open={openNotificationModal}
          handleClose={() => setOpenNotificationModal(false)}
          title="Thank you for your subscription!"
        >
          <Grid item>
            <Avatar
              src="/assets/icons/congrats-icon.gif"
              sx={{ width: 30, height: 30 }}
              alt="Congrats Icon"
            />
          </Grid>
          {!userData?.isVerified && (
            <StyledTypography
              sx={{ fontSize: "1.2rem", color: "text.secondary" }}
            >
              Welcome aboard! and thank you again for your membership
              subscription.
              <br />
              Your subscription payment is pending during this time and will not
              be deducted from your account until we approve your verification.
              You'll receive an email and profile notification once the
              verification process is complete, after which we'll process your
              payment and unlock full access.
              <br />
              Thanks for your patience, and for any questions, contact{" "}
              <strong>support@forus.one</strong>.
            </StyledTypography>
          )}
        </NotificationModal>
      )}

      {showVerificationModal && (
        <AccountVerificationModal
          open={showVerificationModal}
          handleClose={() => setShowVerificationModal(false)}
        />
      )}
    </>
  );
};

SubscriptionModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default SubscriptionModal;
